/**
 * Janus lane MV1 · M4b — the preferred-speed TELEPORT DRAG as client truth (speed_variable 'teleport').
 * plans/janus-lane-mv1-teleport-burrow-squeeze-handoff-2026-09-27.md §5 S2; design plans/janus-m4b-teleport-m3d-burrow-plan-2026-09-24.md §2.
 * Legacy twin: ft3d_move_encounter_creature.php's teleport branch (ft_teleport_destination → price → ft_commit_teleport).
 *
 * Blind (owner ruling 2): no sight check. A landing inside solid or another body DISPLACES to the nearest free spot
 * (shared/solid-material.ts teleportDisplacement; MV6 2026-10-03: the receiver no longer re-derives it — the page decides), and the
 * displacement's unserved 2d10 force per 10 ft is a page-decided, GM-logged skip on the movement commit (R8b).
 *
 * Local refusals (the legacy text, no request): not your turn · the same spot · beyond range · no teleport base · the
 * vertical-space refusal · nowhere free within 60 ft (a shapeless canvas too). Hand-backs: not the preferred speed · grappled / restrained / prone
 * (handle_teleport_status_effects clears them and a restrainer can deny the teleport — legacy owns both) · every flat
 * roster / status / trigger gate · a zone at either end · arrival inside any watcher's reach (a departure never provokes).
 */
import { displacementDice, solidContextFromEncounter, teleportDisplacement, type SolidBody } from '../../shared/solid-material.js';
import { storeMovementPct } from './banked-walk.js';
import type { PlainWalkResult, TeleportClear, WalkPoint } from './plain-walk.js';

type Row = Record<string, unknown>;
export const SPEED_TELEPORT_TYPE_ID = '3C96DE74-D9E6-4173-8671-989209A0BF97';
export const TELEPORT_RANGE_MESSAGE = 'That destination is beyond your teleport range.';
export const TELEPORT_SAME_SPOT_MESSAGE = 'Choose another location.';
export const TELEPORT_NO_SPACE_MESSAGE = 'There is not enough clear space to teleport there.';
export const TELEPORT_VERTICAL_MESSAGE = 'There is not enough vertical space to teleport there.';
const MOVEMENT_EPS = 1e-9;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const bucket = (row: Row, name: string): Row => record(row[name]) ?? {};
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const round3 = (value: number): number => Number(value.toFixed(3));

export interface TeleportReaders {
  value: (row: Row, name: string) => unknown;
  statuses: (row: Row) => Row[];
  hasStatus: (row: Row, name: string) => boolean;
  geometry: (row: Row) => { diameter: number; height: number; rulesDiameter?: number } | null;
  position: (row: Row) => [number, number, number];
  inCombat: (encounter: Row, row: Row) => boolean;
  /** MV6 (R-067): ft_teleport_destination()'s LAST gate — ft_resolve_move_destination(walk, free elevation) places the body at the final spot
   *  with no nudge (furniture is not part of the solid rule: a prop the displacement ignores still refuses the landing). true / false, or
   *  'unknown' when the page cannot prove it (websocket/walk-route-obstacles.ts — the inventory lane's liftDestinationClear twin). */
  landingClear?: (point: WalkPoint, sizeFt: number) => boolean | 'unknown';
  /** A SHAPELESS encounter's landing on its canvas plane (z = 0): the nearest spot clear of prop footprints and standing bodies within the
   *  60 ft cutoff, ordered like teleportDisplacement(). `why` names an unmeasurable input (plain-walk-scope's shapeless walk twins supply it). */
  shapelessLanding?: (landing: WalkPoint, origin: WalkPoint) => { point: WalkPoint; displacedFt: number } | { blocked: true }
    | { why: string; inventory?: string | null };
}
export type TeleportScope = { supported: true; walk: PlainWalkResult }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused?: false; reason: string; detail?: Record<string, unknown> };

/** The legacy drag's economy (ft3d_move_encounter_creature.php): range = base × (bank + 1 when an AP can bank) in combat,
 *  base out of combat; cost = distance ÷ base from the bank, banking ONE AP (+1.0) when the bank is short. */
export function priceTeleport(distanceFt: number, baseFt: number, movementPct: number, aps: number, inCombat: boolean):
  { range: number; movement_pct?: number; aps?: number; apsSpent: number } {
  if (!inCombat) return { range: baseFt, apsSpent: 0 };
  const range = baseFt * (movementPct + (aps > 0 ? 1 : 0));
  const cost = distanceFt / baseFt;
  let pct = movementPct, spent = 0;
  if (cost > pct + MOVEMENT_EPS && aps > 0) { pct += 1; spent = 1; }
  return { range, movement_pct: storeMovementPct(Math.max(0, pct - cost)), aps: aps - spent, apsSpent: spent };
}

/** Legacy text (teleport_destination.php, ft_teleport_denied_by_restrainer). */
export const TELEPORT_BINDING_MESSAGE = 'You cannot teleport out of this binding.';
/** StatusEffect 'restrained'. */
const RESTRAINED_ITEM_ID = 'E34AF232-5438-88FC-9A9F-73850CCCFD51';
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/**
 * MV5 (R-067): twin of ft_teleport_denied_by_restrainer() (restrained_teleport_denial.php — the Bounty Hunter's Binding Expert): the mover is
 * Restrained by at least one creature carrying `restrained_by_caster_cannot_teleport`. A Restrained row's placer is its status_caster_id (an
 * elided one is the target itself — creature_statuses' re-stamp, so a self-restraint counts); an environmental row (no placer) and a placer
 * not on this roster never deny (FAIL OPEN, as PHP).
 */
export function teleportDeniedByRestrainer(encounter: Row, caster: Row, read: Pick<TeleportReaders, 'value' | 'statuses'>): boolean {
  const self = upper(bucket(caster, 'encounter_creatures').encounter_creature_id);
  const placers = new Set<string>();
  for (const status of read.statuses(caster)) {
    if (upper(status.status_item_id ?? bucket(status, 'items').item_id) !== RESTRAINED_ITEM_ID) continue;
    // Codex MV5 r2 P2: only an ELIDED caster id is re-stamped (creature_statuses); an explicitly empty one is environmental — never a placer
    const placer = status.status_caster_id === undefined || status.status_caster_id === null ? (upper(status.status_target_id) || self) : upper(status.status_caster_id);
    if (UUID.test(placer)) placers.add(placer);
  }
  if (!placers.size) return false;
  for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const row = record(raw);
    if (!row || !placers.has(upper(bucket(row, 'encounter_creatures').encounter_creature_id))) continue;
    const flag = scalar(read.value(row, 'restrained_by_caster_cannot_teleport'));
    if (flag !== undefined && flag !== null && flag !== '' && flag !== 0 && flag !== '0' && flag !== false) return true;
  }
  return false;
}

/** The other creatures a landing must not overlap: every row but the mover, as its size-tier cylinder (PHP ft_teleport_other_bodies). */
export function teleportBodies(encounter: Row, actorId: string, read: TeleportReaders): SolidBody[] | null {
  const bodies: SolidBody[] = [];
  for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const row = record(raw);
    if (!row) return null;
    const ec = bucket(row, 'encounter_creatures');
    if (upper(ec.encounter_creature_id) === upper(actorId) || Number(scalar(ec.encounter_creature_deleted) ?? 0) === 1) continue;
    const geometry = read.geometry(row);
    const size = geometry ? (geometry.rulesDiameter ?? geometry.diameter) : NaN;
    if (!(size > 0)) return null;
    const [x, y, z] = read.position(row);
    if (![x, y, z].every(Number.isFinite)) return null;
    bodies.push({ x, y, z, radius: size / 2, height: size });
  }
  return bodies;
}

/**
 * Resolve a teleport drag. `gates` runs the flat walk's roster / status / trigger / zone / reach gates for a teleport
 * (plain-walk-scope passes its own resolver so this module never imports it back).
 */
export function resolveTeleportScope(
  input: { actorId: string; origin: WalkPoint; destination: WalkPoint; force: boolean },
  encounter: Row, caster: Row, read: TeleportReaders,
  gates: (landing: WalkPoint, origin: WalkPoint) => { supported: true } | { supported: false; refused?: boolean; reason?: string; message?: string; detail?: Record<string, unknown> },
  notYourTurn: string,
): TeleportScope {
  const no = (reason: string, detail?: Record<string, unknown>): TeleportScope => (detail ? { supported: false, reason, detail } : { supported: false, reason });
  const refuse = (message: string): TeleportScope => ({ supported: false, refused: true, message });
  const { origin, destination } = input;
  if (input.force || !input.actorId || ![origin.x, origin.y, origin.z, destination.x, destination.y, destination.z].every(Number.isFinite)) return no('route-or-movement-mode');
  const ec = bucket(caster, 'encounter_creatures');
  if (upper(ec.preferred_speed_type) !== SPEED_TELEPORT_TYPE_ID) return no('preferred-speed', { preferred: upper(ec.preferred_speed_type) });
  const inCombat = read.inCombat(encounter, caster);
  if (inCombat && upper(read.value(encounter, 'current_encounter_creature_id_turn')) !== upper(input.actorId)) return refuse(notYourTurn);
  // MV5 (2026-10-03, R-067): Grappled / Restrained / Prone no longer hand back. A teleport ENDS them (handle_teleport_status_effects — a
  // teleport is Restrained's designed escape): the walk carries them as `teleport.clears` and the receiver writes the removals after the commit
  // (movement_solid_truth_inc.php — bound to this creature's own rows). The one denial (ft_teleport_denied_by_restrainer — "creatures restrained
  // by you cannot use teleportation") is decided here and refused locally with the legacy text; a placer the page does not hold fails OPEN, as
  // PHP does. An unreadable status hands back.
  const clears: TeleportClear[] = [];
  for (const key of ['grappled', 'restrained', 'prone'] as const) {
    let has: boolean;
    try { has = read.hasStatus(caster, key); } catch { return no('teleport-status-effects', { status: key, why: 'unreadable' }); }
    if (has) clears.push(key);
  }
  if (clears.includes('restrained')) {
    let denied: boolean;
    try { denied = teleportDeniedByRestrainer(encounter, caster, read); } catch { return no('teleport-status-effects', { status: 'restrained', why: 'unreadable' }); }
    if (denied) return refuse(TELEPORT_BINDING_MESSAGE);
  }
  const base = Number(scalar(read.value(caster, 'teleport')) ?? 0);
  if (!(base > 0)) return no('teleport-speed');
  let shape = bucket(encounter, 'encounters').encounter_shape ?? encounter.encounter_shape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape) as unknown; } catch { return no('shape'); } }
  // A shapeless (legacy 2D) encounter has no solid geometry: judged on the canvas plane (z = 0) like the shapeless walk (plain-walk-scope
  // MV4) — a stored z inside the body's band is a legacy centre or the feet; the landing is the ground.
  const shapeless = !Array.isArray(record(shape)?.boxes) || !(record(shape)!.boxes as unknown[]).length;
  const geometry = read.geometry(caster);
  const planeZ = (z: number): number => (geometry && z >= -1e-6 && z <= 1.5 * geometry.height + 1e-6 ? 0 : z);
  const from = shapeless ? { ...origin, z: planeZ(origin.z) } : origin;
  const to = shapeless ? { ...destination, z: 0 } : destination;
  const distance = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
  if (distance < 0.01) return refuse(TELEPORT_SAME_SPOT_MESSAGE);
  const rawBank = scalar(ec.movement_pct), rawAps = scalar(ec.aps);
  const bank = rawBank === undefined || rawBank === null || rawBank === '' ? 0 : Number(rawBank);
  const aps = rawAps === undefined || rawAps === null || rawAps === '' ? 0 : Number(rawAps);
  if (!Number.isFinite(bank) || bank < 0) return no('bank-shape');
  if (!Number.isSafeInteger(aps) || aps < 0) return no('aps-shape');
  const price = priceTeleport(distance, base, bank, aps, inCombat);
  if (distance > price.range + 0.001) return refuse(TELEPORT_RANGE_MESSAGE);
  const size = geometry ? (geometry.rulesDiameter ?? geometry.diameter) : NaN;
  if (!(size > 0)) return no('mover-geometry');
  const teleportWalk = (final: WalkPoint, displacedFt: number, dice: number): TeleportScope => {
    // The gates judge where the body actually ARRIVES (Codex M4b review): a displacement can carry it into a zone or a reach.
    const gate = gates(final, from);
    if (!gate.supported) return gate.refused ? refuse(String(gate.message ?? '')) : no(String(gate.reason ?? 'gates'), gate.detail);
    const walk: PlainWalkResult = {
      schema: 'janus.movement.m1a.v1', actor_id: input.actorId, origin: { ...origin }, destination: final,
      route: [[origin.x, origin.y, origin.z], [final.x, final.y, final.z]],
      updates: { x_pos: final.x, y_pos: final.y, z_pos: final.z }, speed_cost: inCombat ? distance : 0,
      teleport: { landing: { ...to }, displaced_ft: displacedFt, dice, ...(clears.length ? { clears } : {}) },
    };
    if (!inCombat) return { supported: true, walk };
    return { supported: true, walk: { ...walk, before: { movement_pct: bank, aps },
      updates: { ...walk.updates, movement_pct: price.movement_pct!, aps: price.aps! } } };
  };
  if (shapeless) {
    // Owner 2026-10-21 (fbtelshape): a landing on a body or prop displaces beside it like a shaped room's (2d10 force per 10 ft), never
    // the legacy exact-spot refusal; only nowhere free within the cutoff refuses.
    // The search runs from the EXACT request (Codex fbtelshape review): ordering and the dice are the shaped rule's; only the output rounds.
    const landing = { x: to.x, y: to.y, z: 0 };
    let placed: ReturnType<NonNullable<TeleportReaders['shapelessLanding']>> | 'unknown';
    try { placed = read.shapelessLanding ? read.shapelessLanding(landing, from) : 'unknown'; } catch { placed = 'unknown'; }
    if (placed === 'unknown') return no('teleport-landing-unproven', { landing: { x: round3(to.x), y: round3(to.y), z: 0 }, reader: !!read.shapelessLanding, shapeless: true });
    if ('why' in placed) return no('shapeless', { ...placed, at: 'teleport-landing' });
    if ('blocked' in placed) return refuse(TELEPORT_NO_SPACE_MESSAGE);
    const displacedFt = Math.round(placed.displacedFt * 1000) / 1000;
    return teleportWalk({ x: round3(placed.point.x), y: round3(placed.point.y), z: 0 }, displacedFt, displacementDice(placed.displacedFt));
  }
  const ctx = solidContextFromEncounter(encounter, read.statuses);
  // PHP's kept vertical-space refusal: standing on a room's floor under a roof lower than the body.
  for (const box of ctx.boxes) {
    if (!box.open && destination.x >= box.x.min - 1e-6 && destination.x <= box.x.max + 1e-6 && destination.y >= box.y.min - 1e-6 && destination.y <= box.y.max + 1e-6
      && Math.abs(destination.z - box.floor) <= 0.01 && destination.z < box.ceiling && destination.z + size > box.ceiling + 0.01) return refuse(TELEPORT_VERTICAL_MESSAGE);
  }
  const bodies = teleportBodies(encounter, input.actorId, read);
  if (!bodies) return no('other-geometry');
  const landing = teleportDisplacement(ctx, bodies, origin, destination, size / 2, size);
  if (landing.blocked) return refuse(TELEPORT_NO_SPACE_MESSAGE);
  const final = { x: round3(landing.point.x), y: round3(landing.point.y), z: round3(landing.point.z) };
  // A landing in clear AIR is kept (it is neither solid nor occupied) — and then falls, which is the legacy commit's.
  if (!ctx.boxes.some(box => Math.abs(final.z - box.floor) <= 0.01 && final.x >= box.x.min - 1e-6 && final.x <= box.x.max + 1e-6
    && final.y >= box.y.min - 1e-6 && final.y <= box.y.max + 1e-6)) return no('teleport-airborne', { landing: final });
  // MV6 (2026-10-03, R-067): ft_teleport_destination()'s last gate, decided HERE (the receiver no longer re-derives the landing): the legacy
  // resolver must place the body at the final spot exactly — a prop / furniture footprint it would nudge off refuses with the clear-space text.
  let clear: boolean | 'unknown';
  try { clear = read.landingClear ? read.landingClear(final, size) : 'unknown'; } catch { clear = 'unknown'; }
  if (clear === 'unknown') return no('teleport-landing-unproven', { landing: final, reader: !!read.landingClear });
  if (!clear) return refuse(TELEPORT_NO_SPACE_MESSAGE);
  return teleportWalk(final, landing.displacedFt, landing.dice);
}
